import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {generateSpreadsheetTemplateHeaders} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {NdcSpreadsheetComponentFormatType} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableKind';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

export interface ProteinCreationTableMetadata {
  antibodyFormat: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
}

export type ProteinCreationStructuredTable = StructuredTable<ProteinCreationTableMetadata, RowMetadata>;

export default class ProteinCreationStructuredTableAdapter extends GenericStructuredTableAdapter<
  ProteinCreationTableMetadata,
  RowMetadata
> {
  static getAdapter(): ProteinCreationStructuredTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROTEIN_CREATION
    ] as ProteinCreationStructuredTableAdapter;
  }

  override getActionFunctions(): ActionFunctions<ProteinCreationTableMetadata, RowMetadata> {
    return {secondary: [], overflow: []};
  }

  getBlankRowMetadata(): RowMetadata {
    return {};
  }

  getDisplayName(structuredTable: ProteinCreationStructuredTable): string {
    return structuredTable.name;
  }

  override _getAllSupportedColumns(params: {
    entryId: string | null;
    metadata: ProteinCreationTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const {antibodyFormat, startingType} = params.metadata;
    const domainRequiredByLabel = new Map(
      antibodyFormat.domains.map((domain) => [domain.label, !domain.optional])
    );
    const variablePairLabels = new Set(
      antibodyFormat.distinctVariablePairs.map((variablePair) => variablePair.label)
    );

    const componentColumns = generateSpreadsheetTemplateHeaders(antibodyFormat, startingType).map((name) => ({
      ...makeStructuredTableColumn({
        columnType: TableColumnType.EXISTING_BIOENTITY,
        name,
        // Chains and variable pairs are always required. Only domains may be optional.
        isRequired: domainRequiredByLabel.get(name) ?? true,
      }),
      columnType: TableColumnType.EXISTING_BIOENTITY, // need to re-specify to type narrow
      // A variable pair may be registered as either a DNA pair or an AA pair, and which one it is
      // is not known until the user picks an entity, so pair columns cannot be narrowed further.
      bioentityTypeFilters: variablePairLabels.has(name)
        ? [ModelTypeConstants.BASIC_FOLDER_ITEM]
        : [ModelTypeConstants.SEQUENCE, ModelTypeConstants.PROTEIN],
      frontEndMetadata: {validSchemas: 'ALL'},
    }));

    // Component columns come first so that their order matches the spreadsheet template. Schema field
    // columns from the base adapter trail them, mirroring how the spreadsheet importer treats columns
    // that match no structural label as additional fields.
    return [...componentColumns, ...super._getAllSupportedColumns(params)];
  }

  override toTable(structuredTable: ProteinCreationStructuredTable): Table {
    return {...super.toTable(structuredTable), allowColumnEdits: false};
  }

  override allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }
}
